import {
  GetAvailabilityCalendarResponse,
  GetRoomsPriceResponse,
  GetSearchBarSettingsResponse,
  ListPlansResponse,
  SearchAvailabilityResponse,
} from 'tokyu-hooks/lib/response/hotel'
import {
  AdditionalQuestionModel,
  AvailabilityCalendarModel,
  PlanListItemModel,
  RoomPriceModel,
  RoomSelectInfo,
  SearchResultModel,
  SearchSettingModel,
} from './types'
import { AdditionalQuestionsResponse } from 'tokyu-hooks/lib/response/reservation'
import { SearchTicketsResponse } from 'tokyu-hooks/lib/response/pocketrd/hotel'
import every from 'lodash/every'
import dayjs from 'lib/dayjs'
import {
  HANARE_HOTEL_CONFIG,
  EBISU_HOTEL_CONFIG,
  MERCURE_HIROSHIMA_HOTEL_CONFIG,
  SHINJUKU_OPENING_HOTEL_CONFIG,
  GOTANDA_OPENING_HOTEL_CONFIG,
  RESERVATION_MODE,
  RESERVATION_MODE_ENUM,
} from 'utils/constants'

type PlanListItemData = ListPlansResponse['data'][0]
type SearchResponseData = SearchAvailabilityResponse['data']
type SearchBarSetting = GetSearchBarSettingsResponse['data']
type RoomPriceData = GetRoomsPriceResponse['data']
type AdditionalQuestionData = AdditionalQuestionsResponse['data'][0]
type AvailabilityCalendaData = GetAvailabilityCalendarResponse['data']

export const DEFAULT_SEARCH_RESULT_SUBITEMS_NUMBER = 2

export const DEFAULT_SEARCH_RESULT_SUBITEMS_NUMBER_RESALE_PLANS = 2

export const DEFAULT_SEARCH_RESULT_SUBITEMS_NUMBER_RESALE_ROOMS = 1

export const TOP_HOTEL_SEARCH_RECOMMENDED = 3

export const AREA_OPTION_ALL = 'ALL_AREA'

export const HOTEL_OPTION_ALL = 'ALL'

export const CURRENCY_CODE = 'JPY'

export const HOTEL_SEARCH_ORDER_BY = {
  PRICE_LOW_TO_HIGH: 'price_low_to_high',
  PRICE_HIGH_TO_LOW: 'price_high_to_low',
  RECOMMENDED: 'recommended',
}

export const PAYMENT_SCREEN_BACK_TO = {
  SEARCH_RESULT: 'search_result',
  ROOM_PLAN_DETAIL: 'room_plan_detail',
}

export const HOTEL_SEARCH_ORDER_BY_DEFAULT = HOTEL_SEARCH_ORDER_BY.RECOMMENDED

export const toPlanListItem = (plan: PlanListItemData): PlanListItemModel => ({
  id: plan.id,
  hotelId: plan.hotel_id,
  name: plan.name,
  code: plan.code,
  description: plan.description,
  urlPlanPictures: plan.url_plan_pictures || [],
  minStay: plan.min_stay,
  room_search_filters: plan.room_search_filters?.map((filter) => ({
    id: filter.id,
    name: filter.name,
  })),
})

export const toSearchResultModel = (
  searchResult?: SearchResponseData,
  reservationMode?: RESERVATION_MODE_ENUM,
): SearchResultModel | undefined => {
  if (!searchResult) return searchResult
  return {
    plans: searchResult?.plans?.map((plan) => ({
      filters: plan.filters,
      code: plan.code,
      name: plan.name,
      description: plan.description,
      urlPlanPictures: plan.url_plan_pictures,
      reservationMode,
      rooms: plan?.rooms?.map((room) => ({
        roomPlanCode: room.room_plan_code,
        matchedSearchCriteria: {
          adultsCount: room?.matched_search_criteria?.adults_count,
          childrenCount: room?.matched_search_criteria?.children_count,
        },
        roomTypeCode: room.room_type_code,
        hotelPlanCode: room.hotel_plan_code,
        roomTypeName: room.room_type_name,
        filters: room.filters?.map((filter) => ({
          id: filter.id,
          name: filter.name,
        })),
        signInDiscount: {
          totalPriceAfterDiscount:
            room?.sign_in_discount?.total_price_after_discount,
          totalPriceAfterDiscountTax:
            room?.sign_in_discount?.total_price_after_discount_tax,
          mcpTotalPriceAfterDiscount:
            room?.sign_in_discount?.mcp_total_price_after_discount?.cents,
          mcpTotalPriceAfterDiscountTax:
            room?.sign_in_discount?.mcp_total_price_after_discount_tax?.cents,
          promotions: room?.sign_in_discount?.promotions?.map((promotion) => ({
            id: promotion.id,
            name: promotion.name,
            promotionType: promotion.promotion_type,
          })),
        },
        minPeople: room.min_people,
        maxPeople: room.max_people,
        maxAdult: room.max_adult,
        totalPrice: room.total_price,
        tax: room.tax,
        mcpCurrency: room.mcp_total_price?.currency_iso,
        mcpTotalPrice: room.mcp_total_price?.cents,
        mcpTax: room.mcp_tax?.cents,
        roomCount: room.room_count,
        roomPictures: room.room_pictures.flatMap(
          (picture) => picture?.picture?.url,
        ),
        reservationMode,
      })),
    })),
    rooms: searchResult?.rooms?.map((room) => ({
      roomTypeCode: room.room_type_code,
      roomTypeName: room.room_type_name,
      filters: room.filters?.map((filter) => ({
        id: filter.id,
        name: filter.name,
      })),
      roomPictures: room.room_pictures.flatMap(
        (picture) => picture?.picture?.url,
      ),
      roomCount: room.room_count,
      reservationMode,
      plans: room?.room_plan?.map((plan) => ({
        filters: plan.filters,
        matchedSearchCriteria: {
          adultsCount: plan?.matched_search_criteria?.adults_count,
          childrenCount: plan?.matched_search_criteria?.children_count,
        },
        roomPlanCode: plan.room_plan_code,
        roomTypeCode: plan.room_type_code,
        hotelPlanCode: plan.hotel_plan_code,
        roomPlanName: plan.room_plan_name,
        signInDiscount: {
          totalPriceAfterDiscount:
            plan?.sign_in_discount?.total_price_after_discount,
          totalPriceAfterDiscountTax:
            plan?.sign_in_discount?.total_price_after_discount_tax,
          mcpTotalPriceAfterDiscount:
            plan?.sign_in_discount?.mcp_total_price_after_discount?.cents,
          mcpTotalPriceAfterDiscountTax:
            plan?.sign_in_discount?.mcp_total_price_after_discount_tax?.cents,
          promotions: plan?.sign_in_discount?.promotions?.map((promotion) => ({
            id: promotion.id,
            name: promotion.name,
            promotionType: promotion.promotion_type,
          })),
        },
        totalPrice: plan.total_price,
        tax: plan.tax,
        mcpCurrency: plan.mcp_total_price?.currency_iso,
        mcpTotalPrice: plan.mcp_total_price?.cents,
        mcpTax: plan.mcp_tax?.cents,
        urlPlanPictures: plan.url_plan_pictures,
        reservationMode,
      })),
    })),
  }
}

export const toSearchResultPocketRdModel = (
  pocketRdData?: SearchTicketsResponse,
): SearchResultModel | undefined => {
  if (!pocketRdData) return undefined

  return {
    plans:
      pocketRdData?.searchType === 'plan'
        ? pocketRdData?.reservations.map((reservation) => ({
            code: reservation.groupCode,
            name: reservation.groupName,
            description: '',
            urlPlanPictures: [],
            filters: reservation.reservationContent.filters.map(
              (filter, index) => ({
                id: index,
                name: filter,
              }),
            ),
            reservationMode: RESERVATION_MODE.SECRET,
            isPocketRdResalePlan: true,
            rooms: reservation.reservationContent.rooms.map((room) => ({
              roomPlanCode: room.room_plan_code,
              matchedSearchCriteria: {
                adultsCount: room.adults,
                childrenCount: room.children,
              },
              minPeople: room.min_people,
              maxPeople: room.max_people,
              maxAdult: room.max_people,
              roomTypeCode: room.room_type_code,
              hotelPlanCode: room.hotel_plan_code,
              roomTypeName: room.room_type_name,
              filters: reservation.reservationContent.filters.map(
                (filter, index) => ({
                  id: index,
                  name: filter,
                }),
              ),
              signInDiscount: {
                totalPriceAfterDiscount: 0,
                totalPriceAfterDiscountTax: 0,
                promotions: [],
              },
              totalPrice: 0,
              tax: 0,
              roomCount: 1,
              roomPictures: room.room_pictures.flatMap(
                (data) => data.picture.url,
              ),
              resaleMinPrice: reservation.minPrice,
              resaleTotalGroup: pocketRdData.totalResultsAvailable,
              resaleSubGroupCount: reservation.subGroupCount,
              resaleSearchPageURL: pocketRdData.searchPageURL,
              reservationMode: RESERVATION_MODE.SECRET,
              isPocketRdResalePlan: true,
            })),
          }))
        : undefined,
    rooms:
      pocketRdData?.searchType === 'rooms'
        ? pocketRdData?.reservations.map((reservation) => ({
            roomTypeCode: reservation.groupCode,
            roomTypeName: reservation.groupName,
            filters: reservation.reservationContent.filters.map(
              (filter, index) => ({
                id: index,
                name: filter,
              }),
            ),
            roomCount: reservation.reservationContent.room_count,
            roomPictures:
              reservation.reservationContent.rooms[0].room_pictures.flatMap(
                (data) => data.picture.url,
              ),
            reservationMode: RESERVATION_MODE.SECRET,
            isPocketRdResalePlan: true,
            plans: reservation.reservationContent.rooms.map((room) => ({
              matchedSearchCriteria: {
                adultsCount: room.adults,
                childrenCount: room.children,
              },
              roomPlanCode: room.room_plan_code,
              roomTypeCode: room.room_type_code,
              hotelPlanCode: room.hotel_plan_code,
              roomPlanName: room.room_plan_name,
              filters: reservation.reservationContent.filters.map(
                (filter, index) => ({
                  id: index,
                  name: filter,
                }),
              ),
              signInDiscount: {
                totalPriceAfterDiscount: 0,
                totalPriceAfterDiscountTax: 0,
                promotions: [],
              },
              totalPrice: 0,
              tax: 0,
              urlPlanPictures: room.room_pictures.flatMap(
                (data) => data.picture.url,
              ),
              resaleMinPrice: reservation.minPrice,
              resaleTotalGroup: pocketRdData.totalResultsAvailable,
              resaleSubGroupCount: reservation.subGroupCount,
              resaleSearchPageURL: pocketRdData.searchPageURL,
              reservationMode: RESERVATION_MODE.SECRET,
              isPocketRdResalePlan: true,
            })),
          }))
        : undefined,
  }
}

// Undated search results carry room_count: 0 (no specific room selected yet),
// which would otherwise render an empty room-number selector. Fall back to
// this cap — matching the selector's own Math.min(maxRooms, 4) ceiling — so
// the dropdown still offers choices.
export const DEFAULT_MAX_ROOMS = 4

export const getRoomOptions = (maxRooms: number) => {
  return Array(Math.min(maxRooms, 4))
    .fill(0)
    .map((_, index: number) => ({
      label: (index + 1).toLocaleString('en-US', {
        minimumIntegerDigits: 2,
        useGrouping: false,
      }),
      value: index + 1,
    }))
}

export const toSearchBarSettings = (
  searchBarSettingsData: SearchBarSetting,
): SearchSettingModel => ({
  checkinDateOffset:
    searchBarSettingsData?.search_bar_setting_attributes?.default_search_setting
      ?.checkin_date_offset,
})

export const groupRoomSelectList: (
  roomList: RoomSelectInfo[],
) => RoomSelectInfo[] = (roomList) =>
  roomList.reduce((result: RoomSelectInfo[], item: RoomSelectInfo) => {
    const condition: (r: RoomSelectInfo) => boolean = (r) =>
      r.roomPlanCode === item.roomPlanCode &&
      r.numberOfAdult.toString() === item.numberOfAdult.toString() &&
      r.numberOfChildren.toString() === item.numberOfChildren.toString()

    const currentItem = result.find(condition)

    return currentItem
      ? [
          ...result.filter((room) => !condition(room)),
          {
            ...currentItem,
            number_of_room: currentItem.numberOfRoom + item.numberOfRoom,
          },
        ]
      : [...result, item]
  }, [] as RoomSelectInfo[])

export const toRoomPiceModel = (
  roomPriceData: RoomPriceData,
): RoomPriceModel => ({
  totalPrice: roomPriceData.total_price,
  couponDiscountAmount: roomPriceData.coupon_discount_amount,
  promotionDiscountAmount: roomPriceData.promotion_discount_amount,
  bonusPoint: roomPriceData.bonus_point,
  spendingPointValue: roomPriceData.spending_point_value,
  originalPriceWithTax: roomPriceData.original_price_with_tax,
  totalExtraPrice: roomPriceData.total_extra_price,
  totalPriceWithoutTax: roomPriceData.total_price_without_tax,
  discountedPrice: roomPriceData.discounted_price,
  rooms: roomPriceData.rooms.map((room) => ({
    roomPlanCode: room.room_plan_code,
    roomTypeCode: room.room_type_code,
    adults: room.adults,
    children: room.children,
    totalPrice: room.total_price,
    tax: room.tax,
    couponDiscountAmount: room.coupon_discount_amount,
    rateDetails: room.rate_details,
    roomTypeName: room.room_type_name,
    roomPlanName: room.room_plan_name,
    roomPictures: room.room_pictures.flatMap(
      (picture) => picture?.picture?.url,
    ),
    membershipPromotionId: room.membership_promotion_id,
    discounts: room.discounts.map((d) => ({
      id: d.id,
      name: d.name,
      discountType: d.discount_type,
      discountAmount: d.discount_amount,
      discountAmountWithoutTax: d.discount_amount_without_tax,
    })),
    roomRates: room.room_rates.map((rate) => ({
      date: rate.date,
      tiers: rate.tiers?.map((tier) => ({
        price: tier.price,
        name: tier.name,
      })),
      dayTotalRate: rate.day_total_rate,
    })),
  })),
  paymentMethods: roomPriceData.payment_method.map((p) => ({
    code: p.code,
    name: p.name,
    paymentDiscountAmount: p.payment_discount_amount,
  })),
})

export const toAdditionalQuestionModel = (
  additionalQuestion: AdditionalQuestionData,
): AdditionalQuestionModel => ({
  id: additionalQuestion?.id,
  question: additionalQuestion?.question,
  questionType: additionalQuestion?.question_type,
  answerType: additionalQuestion?.answer_type,
  settings: {
    startedAt: additionalQuestion?.settings?.started_at,
    endedAt: additionalQuestion?.settings?.ended_at,
    timeIntervalValue: additionalQuestion?.settings?.time_interval_value,
    timeIntervalUnit: additionalQuestion?.settings?.time_interval_unit,
    answerOptions: {
      en: additionalQuestion?.settings?.answer_options?.en?.map((item) => ({
        text: item.text,
      })),
      ja: additionalQuestion?.settings?.answer_options?.ja?.map((item) => ({
        text: item.text,
      })),
      ko: additionalQuestion?.settings?.answer_options?.ko?.map((item) => ({
        text: item.text,
      })),
      zh_Hans: additionalQuestion?.settings?.answer_options?.zh_Hans?.map(
        (item) => ({
          text: item.text,
        }),
      ),
      zh_Hant: additionalQuestion?.settings?.answer_options?.zh_Hant?.map(
        (item) => ({
          text: item.text,
        }),
      ),
    },
  },
  hotelPlanCodes: additionalQuestion?.hotel_plan_codes?.map((item) => item),
})

export const toAvailabilityCalendarModel = (
  triplaAvailabilityCalendar: AvailabilityCalendaData,
  pocketRDAvailabilityCalendar?: AvailabilityCalendaData,
): AvailabilityCalendarModel => {
  return {
    id: triplaAvailabilityCalendar?.id,
    disabledDates: pocketRDAvailabilityCalendar?.disabled_dates
      ? (triplaAvailabilityCalendar?.disabled_dates || []).filter((date) =>
          pocketRDAvailabilityCalendar.disabled_dates.includes(date),
        )
      : triplaAvailabilityCalendar?.disabled_dates || [],
  }
}

export const getPlanImagesByLocale = (images?: string[], locale?: string) => {
  if (!images) return undefined

  return images?.filter(
    (picture) =>
      picture.includes(`_${locale}.`) ||
      every(
        ['ja', 'en', 'ko', 'zh-Hans', 'zh-Hant'],
        (lo) => !picture.includes(`_${lo}.`),
      ),
  )
}

export const calculateFinalPrice = (
  totalPrice?: number,
  usedPoint?: number | null,
) => {
  if (!totalPrice) return 0
  return Math.max(0, totalPrice - (usedPoint || 0))
}

export const calculateDiscountAmount = (roomsData: RoomPriceModel['rooms']) => {
  let totalAmount = 0

  for (const room of roomsData) {
    for (const discount of room.discounts) {
      totalAmount += discount.discountAmount
    }
  }

  return totalAmount
}

export const calculateNumberOfDays = (
  startDate: Date | null,
  endDate: Date | null,
) => {
  if (!startDate || !endDate) return 0
  return dayjs(endDate).diff(dayjs(startDate), 'day')
}

export const shouldUseHanareHotelDates = (hotelId?: number): boolean => {
  if (hotelId !== HANARE_HOTEL_CONFIG.id) return false

  const today = dayjs()
  const cutoffDate = dayjs(HANARE_HOTEL_CONFIG.cutoffDate)

  return today.isBefore(cutoffDate)
}

export const shouldUseEbisuHotelDates = (hotelId?: number): boolean => {
  if (hotelId !== EBISU_HOTEL_CONFIG.id) return false

  const today = dayjs()
  const cutoffDate = dayjs(EBISU_HOTEL_CONFIG.cutoffDate)
  return today.isBefore(cutoffDate)
}

export const shouldUseMercureHiroshimaHotelDates = (
  hotelId?: number,
): boolean => {
  if (hotelId !== MERCURE_HIROSHIMA_HOTEL_CONFIG.id) return false

  const today = dayjs()
  const cutoffDate = dayjs(MERCURE_HIROSHIMA_HOTEL_CONFIG.cutoffDate)
  return today.isBefore(cutoffDate)
}

export const shouldUseShinjukuOpeningHotelDates = (
  hotelId?: number,
): boolean => {
  if (hotelId !== SHINJUKU_OPENING_HOTEL_CONFIG.id) return false

  const today = dayjs()
  const cutoffDate = dayjs(SHINJUKU_OPENING_HOTEL_CONFIG.cutoffDate)
  return today.isBefore(cutoffDate)
}

// Gotanda has no numeric id in the config (it is only known by hotelCode),
// so this one matches on hotelCode instead of hotelId.
export const shouldUseGotandaOpeningHotelDates = (
  hotelCode?: string | null,
): boolean => {
  if (hotelCode !== GOTANDA_OPENING_HOTEL_CONFIG.hotelCode) return false

  const today = dayjs()
  const cutoffDate = dayjs(GOTANDA_OPENING_HOTEL_CONFIG.cutoffDate)
  return today.isBefore(cutoffDate)
}
